import {
	Component, Input, OnChanges, SimpleChanges,  ViewChild, AfterViewInit, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { DeviceGroup } from 'DiscoveryEngine.TS/Core';
import { ViewerDeviceGroupDto, Owner } from 'UvExplorerServer.Dtos.TS/Viewer';
import {
	DisplayDeviceGroupTreeDto, DisplayDeviceGroupContainerDto, DisplayDeviceGroupDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { Changeable } from 'common';
import { DiscResultsService } from 'discovery';
import { DeviceGroupEditorComponent } from '../device-group-editor/device-group-editor.component';
import { Subscription } from 'rxjs/Rx';
import { MessageService } from 'base';
import { IdentityService, LicensingService } from 'identity';

enum NavType { None, SelectGroup, AddGroup, DeleteGroup };


@Component({
	selector: 'device-group-list',
	templateUrl: './device-group-list.component.html',
	styleUrls: ['./device-group-list.component.scss']
	//, changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceGroupListComponent implements OnChanges, AfterViewInit, OnDestroy, Changeable {

	@Input() public target: NetViewTreeNodeDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

    @ViewChild(DeviceGroupEditorComponent, { static: true })
	private groupEditor: DeviceGroupEditorComponent;

	public selectedDisplayGroup: DisplayDeviceGroupDto;
	public selectedGroup: ViewerDeviceGroupDto;

	public allGroups: DisplayDeviceGroupDto[];

	private deviceGroupTreeLoadedSub: Subscription;
	//private deviceGroupTreeChangedSub: Subscription;

	constructor(private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private licensingService: LicensingService,
		private changeDetector: ChangeDetectorRef) {

		this.target = null;
		this.resetSelectedGroup();
		this.rebuildGroupList();

		this.deviceGroupTreeLoadedSub = this.discResultsService.onDeviceGroupTreeLoaded.subscribe(() => {
			this.rebuildGroupList();
			this.resetSelectedGroup();
		});
		//this.deviceGroupTreeChangedSub = this.discResultsService.onDeviceGroupTreeChanged.subscribe(() => {
		//	this.rebuildGroupList();
		//	this.resetSelectedGroup();
		//});
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	private get sessionGuid(): string {
		return this.identityService.sessionGuid;
	}

	private get netViewGuid(): string {
		return (this.hasTarget ? this.target.Guid : "");
	}

	private get deviceGroupTree(): DisplayDeviceGroupTreeDto {
		return this.discResultsService.deviceGroupTree;
	}

	private get userGroupsContainer(): DisplayDeviceGroupContainerDto {
		if (this.deviceGroupTree) {
			return this.deviceGroupTree.UserGroupsContainer;
		}
		else {
			return null;
		}
	}

	private onAppliedChange(): void {
		let errorMessage = "Device group save failed";
		if (this.selectedDisplayGroup) {
			this.discResultsService.updateDeviceGroup(this.sessionGuid, this.selectedGroup).subscribe(
				(result: DisplayDeviceGroupDto) => {
					this.rebuildGroupList();
					this.selectedDisplayGroup = result;
					// this.selectedGroup already points to the correct Vdg
					// (i.e., a new Ddg was returned by the service, but the existing Vdg was updated)
					if (this.navType != NavType.None) {
						this.completeNavigation();
						this.resetNavState();
					}
				},
				(error: any) => {
					this.messageService.displayErrorMessage(errorMessage);
				});
		}
		else {
			console.error(errorMessage);
		}
	}

	public ngAfterViewInit(): void {
		return;
	}

	public ngOnDestroy(): void {
		if (this.deviceGroupTreeLoadedSub) {
			this.deviceGroupTreeLoadedSub.unsubscribe();
			this.deviceGroupTreeLoadedSub = undefined;
		}
		//if (this.deviceGroupTreeChangedSub) {
		//	this.deviceGroupTreeChangedSub.unsubscribe();
		//	this.deviceGroupTreeChangedSub = undefined;
		//}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.resetSelectedGroup();
		this.rebuildGroupList();
	}

	private rebuildGroupList(): void {
		this.allGroups = [];

		// Retrieve user groups from the current device group tree
		let _userGroupsContainer = this.userGroupsContainer;
		if (_userGroupsContainer) {
			this.allGroups = _userGroupsContainer.Groups.ToArray();
			this.allGroups.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);
		}
	}

	private navType: NavType = NavType.None;
	private navGroup: DisplayDeviceGroupDto = null;
	public showTempChangesDialog: boolean = false;

	private resetNavState(): void {
		this.navType = NavType.None;
		this.navGroup = null;
		this.showTempChangesDialog = false;
	}

	private onSelectGroup(navGroup: DisplayDeviceGroupDto): void {
		if (this.groupEditor && this.groupEditor.tempChanges) {
			this.navType = NavType.SelectGroup;
			this.navGroup = navGroup;
			this.showTempChangesDialog = true;
			return;
		}

		this.selectGroup(navGroup);
	}

	public onAddGroup(): void {
		if (this.groupEditor && this.groupEditor.tempChanges) {
			this.navType = NavType.AddGroup;
			this.navGroup = null;
			this.showTempChangesDialog = true;
			return;
		}

		this.addGroup();
	}

	private onDeleteGroup(navGroup: DisplayDeviceGroupDto): void {
		// If the currently-selected group is being deleted,
		// there's no need to check for temp changes
		if (navGroup != this.selectedDisplayGroup) {
			if (this.groupEditor && this.groupEditor.tempChanges) {
				this.navType = NavType.DeleteGroup;
				this.navGroup = navGroup;
				this.showTempChangesDialog = true;
				return;
			}
		}

		this.deleteGroup(navGroup);
	}

	public onDiscardTempChanges(): void {
		this.groupEditor.discardTempChanges();
		this.completeNavigation();
		this.resetNavState();
	}

	public get applyTempChangesEnabled(): boolean {
		return (this.groupEditor && this.groupEditor.tempChangesValid);
	}

	// Used in view
	private onApplyTempChanges(): void {
		this.groupEditor.applyTempChanges();
	}

	public onCancelNavigation(): void {
		this.resetNavState();
	}

	private completeNavigation(): void {
		switch (this.navType) {
			case NavType.None:
				// Do nothing
				break;
			case NavType.SelectGroup:
				this.selectGroup(this.navGroup);
				break;
			case NavType.AddGroup:
				this.addGroup();
				break;
			case NavType.DeleteGroup:
				this.deleteGroup(this.navGroup);
				break;
			default:
				throw new Error("Invalid navType");
		}
	}

	private createViewerDeviceGroupDto(netViewGuid: string, name: string): ViewerDeviceGroupDto {
		let dg = new DeviceGroup();
		dg.VerifyGuid();
		dg.Name = name;

		let newVdg = new ViewerDeviceGroupDto();
		newVdg.ScopeGuid = netViewGuid;
		newVdg.Owner = Owner.User;
		newVdg.Image = "";
		newVdg.Name = name;
		newVdg.TagGuid = dg.GUID;
		newVdg.DeviceGroup = dg;

		return newVdg;
	}

	private addGroup(): void {
		let errorMessage = "Device group creation failed";

		if (this.netViewGuid && this.userGroupsContainer) {
			let vdg = this.createViewerDeviceGroupDto(this.netViewGuid, "New Device Group");
			let parentGuid = this.userGroupsContainer.VdgcGuid;
			this.discResultsService.createDeviceGroup(this.sessionGuid, vdg, null, parentGuid, -1).subscribe(
				(result: DisplayDeviceGroupDto) => {
					this.rebuildGroupList();
					this.selectedDisplayGroup = result;
					this.selectedGroup = vdg;
				},
				(error: any) => {
					this.messageService.displayErrorMessage(errorMessage);
				});
		}
		else {
			console.error(errorMessage);
		}
	}

	private deleteGroup(group: DisplayDeviceGroupDto): void {
		let errorMessage = "Device group deletion failed";

		if (this.netViewGuid && this.userGroupsContainer) {
			let parentGuid = this.userGroupsContainer.VdgcGuid;
			this.discResultsService.deleteDeviceGroup(this.sessionGuid, this.netViewGuid,
				group.VdgGuid, parentGuid).subscribe(
				(result: boolean) => {
					this.rebuildGroupList();
					if (this.selectedDisplayGroup == group) {
						// The currently-selected group was deleted
						this.selectedDisplayGroup = null;
						this.selectedGroup = null;
					}
				},
				(error: any) => {
					this.messageService.displayErrorMessage(errorMessage);
				});
		}
		else {
			console.error(errorMessage);
		}
	}

	private selectGroup(group: DisplayDeviceGroupDto): void {
		if (group) {
			this.discResultsService.getDeviceGroup(this.sessionGuid, this.netViewGuid, group.VdgGuid).subscribe(
				(result: ViewerDeviceGroupDto) => {
					this.selectedDisplayGroup = group;
					this.selectedGroup = result;
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve selected device group");
				});
		}
		else {
			this.resetSelectedGroup();
		}
	}

	private resetSelectedGroup(): void {
		this.selectedDisplayGroup = null;
		this.selectedGroup = null;
	}

	//
	// Changeable
	//

	public get tempChanges(): boolean {
		return (this.groupEditor && this.groupEditor.tempChanges);
	}

	// PRE: tempChanges === true
	public get tempChangesValid(): boolean {
		if (this.groupEditor && this.groupEditor.tempChanges) {
			return this.groupEditor.tempChangesValid;
		}
		else {
			console.assert(false, "tempChangesValid called in invalid state");
			return false;
		}
	}

	// PRE: tempChanges === true && tempChangesValid === false
	public get invalidTempChanges(): string[] {
		if (this.groupEditor && this.groupEditor.tempChanges && !this.groupEditor.tempChangesValid) {
			return ["Device Groups"];
		}
		else {
			console.assert(false, "invalidTempChanges called in invalid state");
			return [];
		}
	}

	// PRE: tempChanges === true && tempChangesValid === true
	public applyTempChanges(): void {
		if (this.groupEditor && this.groupEditor.tempChanges && this.groupEditor.tempChangesValid) {
			this.groupEditor.applyTempChanges();
		}
		else {
			console.assert(false, "applyTempChanges called in invalid state");
		}
	}

	// PRE: tempChanges === true
	public discardTempChanges(): void {
		if (this.groupEditor && this.groupEditor.tempChanges) {
			this.groupEditor.discardTempChanges();
		}
		else {
			console.assert(false, "discardTempChanges called in invalid state");
		}
	}

}
